import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Link,
  Container,
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dataService from "@/app/services/dataService";
import SimpleModal from "../shared/simpleModal";
import DateHelper from "@/app/helpers/DateHelper";
import PageLoadingOverlay from "../shared/pageLoadingOverlay";
import { useGlobalNotification } from "../header/GlobalNotificationContext";
import { ERROR_MESSAGE } from "@/app/constants/constants";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import CommonHelper from "@/app/helpers/CommonHelper";
import BorderedInputLansing from "../shared/BorderedInput";

const FIELD_NAMES = {
  EMAIL: "email",
  CONFIRM_EMAIL: "confirmEmail",
  PHONE: "phone",
  CONFIRM_PHONE: "confirmPhone",
} as const;

const SUCCESS_MESSAGE = (
  <>
    Congratulations! Starting today, we'll track your purchases and you'll earn
    rewards to use towards future orders for every $1,000 you spend.
    <br />
    <br />
    Check your email shortly for more information about the IMPACT Rewards
    program and your enrollment. If you have any questions, please email
    HelpNOW@lansingbp.com or contact your local Lansing branch.
  </>
);

const SUCCESS_TITLE = "You're enrolled in IMPACT Rewards";

const Enrollment = () => {
  const queryClient = useQueryClient();
  const { setNotification } = useGlobalNotification();
  const [isEnrolled, setIsEnrolled] = useState<boolean>(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState<boolean>(false);
  const [isSimpleModalOpen, setIsSimpleModalOpen] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string | JSX.Element>("");
  const [modalTitle, setModalTitle] = useState<string>(SUCCESS_TITLE);
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [formData, setFormData] = useState({
    [FIELD_NAMES.EMAIL]: "",
    [FIELD_NAMES.CONFIRM_EMAIL]: "",
    [FIELD_NAMES.PHONE]: "",
    [FIELD_NAMES.CONFIRM_PHONE]: "",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    let val = e.target.value;

    if (field === FIELD_NAMES.PHONE || field === FIELD_NAMES.CONFIRM_PHONE) {
      val = CommonHelper.formatPhone334(val);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: val,
    }));
  };

  const addRewards = useMutation({
    mutationFn: () =>
      dataService.addRewards(
        formData[FIELD_NAMES.EMAIL],
        formData[FIELD_NAMES.PHONE]
      ),
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: ["rewardsEnrollmentStatus"],
        });
        setIsSimpleModalOpen(true);
        setModalMessage(SUCCESS_MESSAGE);
        handleToggleContactModal();
      } else {
        handleToggleContactModal();
        setIsSimpleModalOpen(false);
        setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
      }
    },
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const enrollmentMutation = useMutation({
    mutationFn: async () => {
      return await dataService.rewardsEnrollment();
    },
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: ["rewardsEnrollmentStatus"],
        });
        setModalMessage(
          response.data.result ? SUCCESS_MESSAGE : response.data.reason
        );
        setModalTitle(
          response.data.result ? SUCCESS_TITLE : "Enrollment"
        );
        setIsSimpleModalOpen(true);
      } else {
        setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
      }
    },
  });

  const handleSave = () => {
    const email = formData[FIELD_NAMES.EMAIL].trim();
    const confirmEmail = formData[FIELD_NAMES.CONFIRM_EMAIL].trim();
    const phone = formData[FIELD_NAMES.PHONE].trim();
    const confirmPhone = formData[FIELD_NAMES.CONFIRM_PHONE].trim();

    setValidationMessage("");

    if (email) {
      if (!CommonHelper.validateEmail(email)) {
        setValidationMessage("Invalid email address");
        return;
      }
      if (email !== confirmEmail) {
        setValidationMessage("Email adresses are not the same.");
        return;
      }
    } else if (confirmEmail) {
      setValidationMessage(
        "Please enter an email address to match the confirmation"
      );
      return;
    }

    if (phone) {
      if (!CommonHelper.validatePhone(phone)) {
        setValidationMessage("Phone number is not valid.");
        return;
      }
      if (phone !== confirmPhone) {
        setValidationMessage("Phone numbers do not match");
        return;
      }
    } else if (confirmPhone) {
      setValidationMessage(
        "Please enter a phone number to match the confirmation"
      );
      return;
    }

    if (!email && !phone) {
      setValidationMessage(
        "Please provide at least an email or a phone number"
      );
      return;
    }

    addRewards.mutate();
  };

  const { data: enrollmentStatusData, isLoading: enrollmentStatusLoading } =
    useQuery({
      queryKey: ["rewardsEnrollmentStatus"],
      queryFn: () => dataService.rewardsEnrollmentStatus(),
    });

  useEffect(() => {
    if (enrollmentStatusData && !enrollmentStatusData.success) {
      setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);

      return;
    }

    if (
      enrollmentStatusData &&
      enrollmentStatusData.data.enrollmentDate !== null
    ) {
      setIsEnrolled(true);
    }
  }, [enrollmentStatusData, setNotification]);

  const handleToggleContactModal = () =>
    setIsContactModalOpen(!isContactModalOpen);

  return (
    <>
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          mt: 4,
          textAlign: isEnrolled ? "center" : "left",
          fontFamily: "sans-serif",
        }}
      >
        {isEnrolled && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Enrolled since{" "}
              {DateHelper.toDisplayDate(
                enrollmentStatusData?.data.enrollmentDate ?? null
              )}
            </Typography>

            <Button
              variant="contained"
              onClick={handleToggleContactModal}
              sx={{
                backgroundColor: "#43a047",
                "&:hover": { backgroundColor: "#388e3c" },
                textTransform: "uppercase",
                fontWeight: "bold",
                px: 4,
                py: 1,
                mb: 3,
                borderRadius: "4px",
              }}
            >
              + Add Impact Rewards Specific Contact Information
            </Button>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  textAlign: "left",
                  width: "fit-content",
                  maxWidth: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "baseline" },
                    gap: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: { sm: "130px" },
                      color: "text.secondary",
                    }}
                  >
                    Current Email:
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {enrollmentStatusData?.data.email}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "baseline" },
                    gap: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: { sm: "130px" },
                      color: "text.secondary",
                    }}
                  >
                    Current Phone:
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {CommonHelper.formatPhone334(
                      enrollmentStatusData?.data.phoneNumber || ""
                    ) || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ textAlign: "left", mt: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Who qualifies for Lansing IMPACT Rewards?
          </Typography>
          <Typography>
            Customers who are in good credit standing and are not enrolled in
            any special pricing program are eligible to enroll.
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Customers who receive special pricing or discounts through up-front
            pricing plans, rebates, or bid pricing are not eligible to enroll in
            IMPACT Rewards.
          </Typography>
        </Box>

        <Box sx={{ textAlign: "left", mt: 2 }}>
          <Typography sx={{ mb: 2 }}>
            Questions? Contact{" "}
            <Link
              href="mailto:HelpNOW@lansingbp.com"
              underline="hover"
              sx={{ color: "#0057B8" }}
            >
              HelpNOW@lansingbp.com
            </Link>
          </Typography>
          {!isEnrolled && (
            <Button
              className="darkBlueButtonSolid"
              disabled={enrollmentMutation.isPending}
              sx={{ mb: 3 }}
              onClick={() => enrollmentMutation.mutate()}
            >
              ENROLL NOW
            </Button>
          )}
        </Box>
      </Container>

      <Dialog
        open={isContactModalOpen}
        onClose={handleToggleContactModal}
        fullWidth={isMobile}
        maxWidth="sm"
      >
        <DialogContent>
          <Typography
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              mb: 4,
              mt: 1,
              fontSize: "1.1rem",
              color: "#333",
            }}
          >
            NEW CONTACT INFORMATION
          </Typography>

          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, px: 1 }}
          >
            {/* Email Section */}

            <Box sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Email Addresses
              </Typography>
              <BorderedInputLansing
                name="userEmail"
                value={formData[FIELD_NAMES.EMAIL]}
                onChange={(e) => handleInputChange(e, FIELD_NAMES.EMAIL)}
              />
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Confirm Email Addresses
              </Typography>
              <BorderedInputLansing
                name="confirmEmailAddresses"
                value={formData[FIELD_NAMES.CONFIRM_EMAIL]}
                onChange={(e) =>
                  handleInputChange(e, FIELD_NAMES.CONFIRM_EMAIL)
                }
              />
            </Box>

            {/* OR Divider */}
            <Box sx={{ display: "flex", alignItems: "center", my: 1 }}>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "#d9d9d9" }} />
              <Typography
                sx={{
                  px: 2,
                  fontStyle: "italic",
                  fontSize: "0.8rem",
                }}
              >
                OR
              </Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "#d9d9d9" }} />
            </Box>

            {/* Phone Section */}

            <Box sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Cell Phone Number
              </Typography>
              <BorderedInputLansing
                name="cellPhoneNumber"
                value={formData[FIELD_NAMES.PHONE]}
                onChange={(e) => handleInputChange(e, FIELD_NAMES.PHONE)}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Confirm Cell Number
              </Typography>
              <BorderedInputLansing
                name="confirmCellNumber"
                value={formData[FIELD_NAMES.CONFIRM_PHONE]}
                onChange={(e) =>
                  handleInputChange(e, FIELD_NAMES.CONFIRM_PHONE)
                }
              />
            </Box>

            <Typography
              variant="caption"
              sx={{
                textAlign: "center",
                mt: 2,
                fontStyle: "italic",
                lineHeight: 1.3,
              }}
            >
              Any emails or cell numbers entered here will be used for IMPACT
              Rewards notifications only. This will be effective immediately
              going forward. To add multiple emails to an account, separate with
              a comma.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pb: 4,
          }}
        >
          <Typography sx={{ color: "red", mb: 2, fontSize: "0.9rem" }}>
            {validationMessage}
          </Typography>
          <Button
            onClick={handleSave}
            className="darkBlueButtonSolid"
            sx={{ py: 1, px: 6, fontWeight: "bold" }}
          >
            SAVE
          </Button>
        </DialogActions>
      </Dialog>

      <SimpleModal
        isOpen={isSimpleModalOpen}
        onClose={() => setIsSimpleModalOpen(false)}
        message={modalMessage}
        title={modalTitle}
        confirmButtonText="GOT IT"
      />
      {(addRewards.isPending || enrollmentStatusLoading) && (
        <PageLoadingOverlay />
      )}
      {enrollmentMutation.isPending && (
        <PageLoadingOverlay text="Enrolling..." />
      )}
    </>
  );
};

export default Enrollment;
